'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { RoomAudioRenderer, RoomContext } from '@livekit/components-react';
import { Room, RoomEvent } from 'livekit-client';
import { CallHistoryProvider } from './CallHistoryProvider';
/**
 * Browser-side LiveKit room primitive. Owns the Room instance, its lifecycle,
 * and audio playback. Provides RoomContext so descendants can use hooks like
 * useTranscriptions, useLocalParticipant, useMediaDeviceSelect.
 *
 * The Room is created once via useMemo and stays mounted across session
 * changes, so descendants (for example a canvas editor) do not re-parent
 * when the call starts.
 */
export function WebCallRoom({ session, onLeave, onError, children, }) {
    const room = useMemo(() => new Room({
        audioCaptureDefaults: {
            echoCancellation: true,
            noiseSuppression: true,
            // Browsers auto-gain-control ducks soft passages mid-word, which
            // drops the leading phonemes and breaks STT. Turn it off.
            autoGainControl: false,
        },
    }), []);
    useEffect(() => {
        if (!session)
            return;
        let cancelled = false;
        const connect = async () => {
            try {
                await room.connect(session.url, session.token);
                if (cancelled)
                    return;
                // The Room instance is owned here rather than passed to <LiveKitRoom>,
                // so the mic is published explicitly instead of by the wrapper's
                // audio prop.
                await room.localParticipant.setMicrophoneEnabled(true);
            }
            catch (error) {
                if (cancelled)
                    return;
                const message = error instanceof Error ? error.message : 'Room connection failed';
                onError?.(message);
                onLeave();
            }
        };
        void connect();
        return () => {
            cancelled = true;
            void room.disconnect();
        };
    }, [session, room, onLeave, onError]);
    useEffect(() => {
        const onDisconnected = () => onLeave();
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.Disconnected, onDisconnected);
        };
    }, [room, onLeave]);
    useEffect(() => {
        return () => {
            void room.disconnect();
        };
    }, [room]);
    return (_jsxs(RoomContext.Provider, { value: room, children: [_jsx(RoomAudioRenderer, {}), _jsx(CallHistoryProvider, { sessionKey: session?.room ?? null, children: children })] }));
}
//# sourceMappingURL=WebCallRoom.js.map